import * as AddToBasket from '@ori-events/add-to-basket';
import * as LoyaltyDialogResult from '@ori-events/loyalty-dialog-result';
import * as ShowLoyaltyDialog from '@ori-events/show-loyalty-dialog';
import { Dialog } from '@ori-ui/components';
import { Button, Grid } from '@ori-ui/mui';
import { FormatCurrency } from '@ori/culture-provider';
import React, { useCallback, useState } from 'react';

import { BuyForPointsButton } from './components/BuyForPointsButton';
import DialogContent from './components/DialogContent';
import DialogHeader from './components/DialogHeader';
import { AppContextProvider } from './context';
import type { DialogAppProps } from './types';

const DialogApp: React.VFC<DialogAppProps> = ({ translations }) => {
  const [data, setData] = useState<ShowLoyaltyDialog.Data | undefined>();

  React.useEffect(() => {
    const showLoyaltyDialogCallback = ((event: ShowLoyaltyDialog.Event) => {
      setData(event.detail);
    }) as EventListener;

    window.addEventListener(ShowLoyaltyDialog.Name, showLoyaltyDialogCallback);

    return () => window.removeEventListener(ShowLoyaltyDialog.Name, showLoyaltyDialogCallback);
  }, []);

  const handleLoyaltyDialogResult = useCallback(
    (result: LoyaltyDialogResult.Result) => {
      if (data) {
        const event = new CustomEvent<LoyaltyDialogResult.Data>(LoyaltyDialogResult.Name, {
          detail: {
            ...data,
            result,
          },
        });
        window.dispatchEvent(event);
        // unmountDialog
        setData(undefined);
      }
    },
    [data],
  );

  const handleClose = useCallback(
    () => handleLoyaltyDialogResult(LoyaltyDialogResult.LoyaltyDialogResults.CLOSE),
    [handleLoyaltyDialogResult],
  );

  const handleBuyForPoints = useCallback(
    () => handleLoyaltyDialogResult(AddToBasket.Method.BUY_FOR_POINTS),
    [handleLoyaltyDialogResult],
  );

  const handleBuyForMoney = useCallback(
    () => handleLoyaltyDialogResult(AddToBasket.Method.BUY_FOR_MONEY),
    [handleLoyaltyDialogResult],
  );

  if (!data) {
    return null;
  }

  return (
    <AppContextProvider
      translations={translations}
      data={data}
    >
      <Dialog
        open
        onClose={handleClose}
      >
        <DialogHeader points={data.loyaltyPoints} />
        <DialogContent
          actions={
            <Grid
              container
              direction="column"
              spacing={3}
            >
              <Grid item>
                <BuyForPointsButton onClick={handleBuyForPoints} />
              </Grid>
              <Grid item>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={handleBuyForMoney}
                >
                  <FormatCurrency>{data.consultantPrice ?? 0}</FormatCurrency>
                </Button>
              </Grid>
            </Grid>
          }
        />
      </Dialog>
    </AppContextProvider>
  );
};

export default DialogApp;
